const API_BASE = '/api/v2';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const json: ApiResponse<T> = await response.json();

  if (!json.success) {
    throw new Error(json.error?.message || 'Request failed');
  }

  return json.data as T;
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<{ user: { id: string; email: string; name?: string; role: 'user' | 'admin' } }>(
        '/auth/login',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        }
      ),

    signup: (email: string, password: string, name?: string) =>
      request<{ user: { id: string; email: string; name?: string } }>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      }),

    logout: () =>
      request<void>('/auth/logout', {
        method: 'POST',
      }),

    me: () =>
      request<{ id: string; email: string; name?: string; role: 'user' | 'admin'; profilePictureUrl?: string }>(
        '/auth/me'
      ),
  },

  dreams: {
    list: (page = 1, pageSize = 20) =>
      request<{
        items: Dream[];
        total: number;
        page: number;
        pageSize: number;
        hasMore: boolean;
      }>(`/dreams?page=${page}&pageSize=${pageSize}`),

    get: (id: string) => request<DreamWithDetails>(`/dreams/${id}`),

    create: (dream: CreateDreamInput) =>
      request<{ id: string; status: string }>('/dreams', {
        method: 'POST',
        body: JSON.stringify(dream),
      }),

    update: (id: string, updates: Partial<CreateDreamInput>) =>
      request<{ id: string }>(`/dreams/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),

    delete: (id: string) =>
      request<void>(`/dreams/${id}`, {
        method: 'DELETE',
      }),

    getStatus: (id: string) => request<{ status: string }>(`/dreams/${id}/status`),

    analyze: (id: string) =>
      request<{ status: string }>(`/dreams/${id}/analyze`, {
        method: 'POST',
      }),

    generateImage: (id: string) =>
      request<{ queued: boolean }>(`/dreams/${id}/image`, {
        method: 'POST',
      }),
  },

  subconscious: {
    listSessions: () => request<SubconsciousSession[]>('/subconscious/sessions'),

    getSession: (id: string) =>
      request<{ session: SubconsciousSession; messages: ChatMessage[] }>(`/subconscious/sessions/${id}`),

    startSession: (dreamId: string, userTwinName?: string) =>
      request<{ sessionId: string }>('/subconscious/sessions', {
        method: 'POST',
        body: JSON.stringify({ dreamId, userTwinName }),
      }),

    sendMessage: (sessionId: string, content: string) =>
      request<{ messageId: string }>(`/subconscious/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }),

    pause: (sessionId: string) =>
      request<void>(`/subconscious/sessions/${sessionId}/pause`, {
        method: 'POST',
      }),

    resume: (sessionId: string) =>
      request<void>(`/subconscious/sessions/${sessionId}/resume`, {
        method: 'POST',
      }),

    recordMeditation: (
      sessionId: string,
      data: { duration: number; calmScore: number; focusScore: number; museSessionId?: string }
    ) =>
      request<{ meditationUnlocked: boolean }>(`/subconscious/sessions/${sessionId}/meditation`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getMemes: (sessionId: string) => request<DreamMeme[]>(`/subconscious/sessions/${sessionId}/memes`),
  },

  symbols: {
    list: (page = 1, pageSize = 50) =>
      request<{ items: Symbol[]; page: number; pageSize: number }>(`/symbols?page=${page}&pageSize=${pageSize}`),

    get: (id: string) => request<Symbol>(`/symbols/${id}`),

    getByName: (name: string) => request<Symbol>(`/symbols/name/${name}`),

    getTop: (limit = 30) => request<{ id: string; name: string; dreamCount: number }[]>(`/symbols/top?limit=${limit}`),

    getArchetypes: () => request<Archetype[]>('/symbols/archetypes'),
  },

  users: {
    getProfile: () => request<UserProfile>('/users/profile'),

    updateProfile: (updates: Partial<ProfileUpdate>) =>
      request<void>('/users/profile', {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),

    getStats: () =>
      request<{
        totalDreams: number;
        publicDreams: number;
        privateDreams: number;
        averageStressScore: number;
      }>('/users/stats'),

    registerPushSubscription: (subscription: {
      endpoint: string;
      p256dh: string;
      auth: string;
      deviceType?: string;
    }) =>
      request<void>('/users/push-subscription', {
        method: 'POST',
        body: JSON.stringify(subscription),
      }),
  },

  readings: {
    getDreamerAnalysis: () => request<DreamerAnalysis | null>('/readings/dreamer-analysis'),

    generateDreamerAnalysis: () =>
      request<{ queued: boolean }>('/readings/dreamer-analysis/generate', {
        method: 'POST',
      }),
  },
};

// Types
interface Dream {
  id: string;
  userId: string;
  createdAt: string;
  rawContent: string;
  dreamType: 'sleeping_dream' | 'future_dream';
  status: 'new' | 'start' | 'run' | 'img' | 'complete';
  visibility: 'public' | 'private';
  howYouFeel?: string;
  stressCode?: number;
}

interface DreamReading {
  id: string;
  title: string;
  shortSummary: string;
  longSummary: string;
  interpretation: string;
  symbols: string[];
  archetypes: string[];
}

interface DreamImage {
  id: string;
  url: string;
  urlLarge?: string;
}

interface DreamWithDetails extends Dream {
  reading?: DreamReading;
  images?: DreamImage[];
}

interface CreateDreamInput {
  rawContent: string;
  dreamType?: 'sleeping_dream' | 'future_dream';
  visibility?: 'public' | 'private';
  sleepDatetime?: string;
  wakeDatetime?: string;
  howYouFeel?: string;
  yourInterpretation?: string;
  notablePeople?: string;
  notableObjects?: string;
  notableLocations?: string;
  stressCode?: number;
}

interface SubconsciousSession {
  id: string;
  dreamId: string;
  status: 'active' | 'paused' | 'completed';
  dreamScenario?: string;
  dreamCharacters?: Array<{ id: string; name: string; role: string; personality: string }>;
  currentMilestone?: string;
  meditationUnlocked?: boolean;
}

interface ChatMessage {
  id: string;
  createdAt: string;
  senderType: 'user' | 'user_twin' | 'dream_character' | 'narrator' | 'system';
  senderName?: string;
  content: string;
}

interface DreamMeme {
  id: string;
  topText?: string;
  bottomText?: string;
  imageUrl?: string;
  animationUrl?: string;
}

interface Symbol {
  id: string;
  name: string;
  dreamCount: number;
  meaningJung?: string;
  meaningHillman?: string;
  meaningClassical?: string;
  meaningGreek?: string;
}

interface Archetype {
  id: string;
  name: string;
  description: string;
  meaning: string;
  dreamCount: number;
}

interface UserProfile {
  id: string;
  email: string;
  name?: string;
  city?: string;
  state?: string;
  country?: string;
  timezone?: string;
  profilePictureUrl?: string;
  visibility: 'public' | 'private';
  subscription?: {
    status: string;
    planId: string;
    nextBillingDate?: string;
  };
}

interface ProfileUpdate {
  name: string;
  city: string;
  state: string;
  country: string;
  timezone: string;
  visibility: 'public' | 'private';
}

interface DreamerAnalysis {
  id: string;
  embodiedArchetype: string;
  dreamPatternAssessment: string;
  dreamPatternNeurosis?: string;
  unseenShadow?: string;
  aspirationalArchetype?: string;
  nextEvolutionarySteps?: string;
  specificAdvice?: string;
}
